import React, { useState } from 'react';
import PropTypes from 'prop-types';
import strings from 'localization/strings';
import { useDispatch, useSelector } from 'react-redux';
import { handleResponse } from 'actions/actionHelpers';
import { required } from 'form/validation';
import clsx from 'clsx';
import { makeStyles } from 'styles/util';
import routes from 'routes';
import { uploadMedia } from 'actions/media';
import arrayMutators from 'final-form-arrays';
import mediaTypes from 'enums/mediaTypes';
import allConversationCategories from 'enums/conversationCategories';

import Form from 'form/Form';
import MessageConsent from './MessageConsent';
import { TextField, Checkboxes, showErrorOnBlur } from 'mui-rff';
import { FieldArray } from 'react-final-form-arrays';
import Dialog from '@material-ui/core/Dialog/Dialog';
import DialogContent from '@material-ui/core/DialogContent/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogActions from '@material-ui/core/DialogActions/DialogActions';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Text from 'common/Text';
import MarginBox from 'common/MarginBox';
import Button from '@material-ui/core/Button';
import MenuItem from '@material-ui/core/MenuItem';
import ImageDropzone from 'components/dropzone/ImageDropzone';
import ImageList from 'components/dropzone/ImageList';
import MediaUploadErrorDialog from 'components/dropzone/MediaUploadErrorDialog';

const useStyles = makeStyles(({ colors }) => ({
    hidden: {
        display: 'none'
    },
    dropzone: {
        color: colors.darkGrey,
        fontSize: '80%'
    },
}));

const MessageDialog = ({ title, content, conversationCategories, selectedConversationCategory, showSendSmsCheckbox, open, onOk, onClose }) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const authenticationContext = useSelector(state => state.authentication.context);

    const initialValues = {
        body: undefined,
        conversationCategory: selectedConversationCategory?.key,
        sendSms: false
    };

    const hasMultipleConversationCategories = conversationCategories.length > 1;

    const [consentGiven, setConsentGiven] = useState(authenticationContext.isSuperAdminOrImpersonated);
    const [uploadErrors, setUploadErrors] = useState([]);
    const [uploadErrorDialogOpen, setUploadErrorDialogOpen] = useState(false);

    const handleFormSubmit = values => {
        onOk({
            body: values.body,
            category: values.conversationCategory ?? allConversationCategories.general.key,
            images: values.images,
            sendSms: values.sendSms
        });
    };

    const handleMediaDrop = (acceptedFiles, fileRejections, fields, mediaType, maxFiles, onUploadSuccess) => {
        const acceptedMedias = acceptedFiles.map((o, i) => ({
            loading: true,
            index: fields.length + i
        }));
        setUploadErrors(fileRejections);
        setUploadErrorDialogOpen(fileRejections.length > 0);

        acceptedMedias.forEach((media, i) => {
            const file = acceptedFiles[i];
            fields.push(media);

            dispatch(uploadMedia(file, mediaType))
                .then(handleResponse(
                    response => {
                        const json = response.payload;
                        const mediaUrl = json.location;
                        const baseUrl = json.baseUrl;
                        if (json.success && mediaUrl) {
                            const newMedia = {
                                mediaUrl,
                                baseUrl,
                                loading: false
                            };
                            if(onUploadSuccess) {
                                onUploadSuccess(json, newMedia);
                            }
                            fields.update(media.index, newMedia);
                            if(media.index >= maxFiles) {
                                fields.remove(0);
                            }
                        } else {
                            fields.remove(media.index);
                        }
                    }
                ));
        });
    };

    const handleUploadErrorDialogClose = () => {
        setUploadErrorDialogOpen(false);
    };

    const getConversationCategoryInstruction = conversationCategoryKey => {
        const category = conversationCategories.find(c => c.key === conversationCategoryKey);
        return strings.formatString(category?.instruction ?? '', routes.contactUs);
    };

    const getConversationCategory = conversationCategoryKey => conversationCategories.find(c => c.key === conversationCategoryKey);

    return (
        <Dialog maxWidth="sm" fullWidth open={open}>
            <Form
                initialValues={initialValues}
                mutators={arrayMutators}
                onSubmit={handleFormSubmit}
            >
                {({ values, handleSubmit, invalid }) => (
                    <form onSubmit={handleSubmit}>
                        <DialogTitle disableTypography>
                            <Typography variant="h5">
                                {title}
                            </Typography>
                        </DialogTitle>
                        <DialogContent>
                            {
                                content &&
                                (
                                    <Typography variant="body1" gutterBottom>
                                        {content}
                                    </Typography>
                                )
                            }
                            {
                                hasMultipleConversationCategories &&
                                (
                                    <TextField
                                        select
                                        name="conversationCategory"
                                        label={strings.conversationCategoryTitle}
                                        variant="outlined"
                                    >
                                        {
                                            Object.values(conversationCategories).map(o => (
                                                <MenuItem key={o.key} value={o.key}>
                                                    {o.title}
                                                </MenuItem>
                                            ))
                                        }
                                    </TextField>
                                )
                            }
                            <Box className={clsx({ [classes.hidden]: hasMultipleConversationCategories && !values.conversationCategory })}>
                                <MarginBox top={2} bottom={2}>
                                    <Text html={getConversationCategoryInstruction(values.conversationCategory)} />
                                </MarginBox>
                                <TextField
                                    name="body"
                                    variant="outlined"
                                    label={strings.newMessagePlaceholder}
                                    showError={showErrorOnBlur}
                                    rows={3}
                                    multiline
                                    fullWidth
                                    required
                                    fieldProps={{ validate: required }}
                                />
                                {
                                    getConversationCategory(values.conversationCategory)?.allowUploadImages &&
                                    (
                                        <FieldArray name="images">
                                            {({ fields }) => (
                                                <>
                                                    <ImageList fields={fields} />
                                                    <ImageDropzone fields={fields} mediaType={mediaTypes.messageImage} onDrop={handleMediaDrop} className={classes.dropzone}>
                                                        {getConversationCategory(values.conversationCategory)?.imageDropzoneText}
                                                    </ImageDropzone>
                                                    <MediaUploadErrorDialog
                                                        uploadErrors={uploadErrors}
                                                        open={uploadErrorDialogOpen}
                                                        onClose={handleUploadErrorDialogClose}
                                                    />
                                                </>
                                            )}
                                        </FieldArray>
                                    )
                                }
                                {
                                    showSendSmsCheckbox &&
                                    (
                                        <Checkboxes
                                            name="sendSms"
                                            data={
                                                { label: strings.alsoNotifyBySms, value: true }
                                            }
                                        />
                                    )
                                }
                                {
                                    !authenticationContext.isSuperAdminOrImpersonated &&
                                    (
                                        <MessageConsent
                                            onConsentToggled={value => setConsentGiven(value)}
                                        />
                                    )
                                }
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            {
                                onOk &&
                                (
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        disabled={invalid || !consentGiven}
                                    >
                                        {strings.send}
                                    </Button>
                                )
                            }
                            {
                                onClose &&
                                (
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        onClick={onClose}
                                    >
                                        {strings.cancel}
                                    </Button>
                                )
                            }
                        </DialogActions>
                    </form>
                )}
            </Form>
        </Dialog>
    );
};

MessageDialog.propTypes = {
    title: PropTypes.string.isRequired,
    content: PropTypes.string,
    conversationCategories: PropTypes.array.isRequired,
    selectedConversationCategory: PropTypes.object,
    showSendSmsCheckbox: PropTypes.bool,
    open: PropTypes.bool.isRequired,
    onOk: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
};

export default MessageDialog;
